import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, GlobalStyles, Link, Stack, Typography, useScrollTrigger } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import BuildCircleRoundedIcon from '@mui/icons-material/BuildCircleRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import GppGoodRoundedIcon from '@mui/icons-material/GppGoodRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import TollRoundedIcon from '@mui/icons-material/TollRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded';
import BrandMark from '../components/BrandMark';
import HeroDemo from '../components/landing/HeroDemo';
import Reveal, { useInView, usePrefersReducedMotion } from '../components/landing/Reveal';
import { isAuthenticated } from '../services/apiClient';
import { BRAND } from '../theme';
import { LEGAL } from '../legal';

const MAKER = {
  name: 'Sajal Mandrekar',
  initials: 'SM',
  linkedIn: 'https://www.linkedin.com/in/sajal-mandrekar-780382194/',
};

const INK = '#0B0E1A';
const GRADIENT_TEXT = {
  background: 'linear-gradient(92deg, #A5B4FC 0%, #38BDF8 45%, #C4B5FD 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
};
const CONTAINER = { maxWidth: 1180, mx: 'auto', px: { xs: 2, sm: 3, md: 4 } };

const marquee = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`;
const blink = keyframes`
  50% { opacity: 0; }
`;
const enter = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: none; }
`;
const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-10px); }
`;

const PROMPTS_A = [
  'Create a task to review the quarterly report, high priority, due Friday',
  "What's due this week?",
  'Log 45 minutes on frontend development',
  'Mark “Send invoice” as done',
  'Which high-priority tasks are still open?',
  'Rename “personal” to “Home”',
];
const PROMPTS_B = [
  'How much time did I log yesterday?',
  'Push the dentist appointment to Monday',
  'Create a category for Learning',
  'Log 2 hours on API integration today',
  'Move my blog draft into Side project',
  'Delete all my completed tasks',
];

const STEPS = [
  {
    icon: <ChatBubbleOutlineRoundedIcon />,
    title: 'Say what you need',
    text: 'Type it the way you would tell a colleague. No forms, no menus, no syntax to learn.',
  },
  {
    icon: <BuildCircleRoundedIcon />,
    title: 'The agent picks its tools',
    text: 'It looks up your categories and tasks, then creates, updates or logs exactly what you asked for — and checks with you before deleting in bulk.',
  },
  {
    icon: <BoltRoundedIcon />,
    title: 'Your workspace updates',
    text: 'Tasks, time logs, categories and analytics refresh instantly. Every change is right there to review.',
  },
];

const TRUST = [
  {
    icon: <LockRoundedIcon />,
    title: 'Your data stays yours',
    text: 'Every assistant action runs inside your account. Its tools cannot see or touch anyone else’s tasks.',
  },
  {
    icon: <GppGoodRoundedIcon />,
    title: 'An assistant that stays on task',
    text: 'A scope and safety check reads each message first. Off-topic, harmful and prompt-injection attempts are stopped before the AI acts.',
  },
  {
    icon: <HelpOutlineRoundedIcon />,
    title: 'Asks before it wipes',
    text: 'Bulk or ambiguous deletes need your confirmation. The assistant never guesses which tasks to throw away.',
  },
  {
    icon: <TollRoundedIcon />,
    title: 'Transparent AI credits',
    text: 'See exactly how much assistant use you have left this week, and when it resets — right from your account menu.',
  },
  {
    icon: <BlockRoundedIcon />,
    title: 'No ads. No trackers.',
    text: 'No advertising or analytics cookies, and your data is never sold. Hosted on Microsoft Azure in India.',
  },
  {
    icon: <DeleteForeverRoundedIcon />,
    title: 'Leave anytime, cleanly',
    text: 'Delete your account from your profile and your tasks, time logs and conversations go with it.',
  },
];

// Milestones from the project's own git history.
const BUILD_LOG = [
  { hash: 'a1743f9', date: 'Jan 2026', text: 'First release — sign-in, to-dos and time logs on a Django REST API' },
  { hash: '658e4c8', date: 'Feb 2026', text: 'An AI assistant arrives, with memory across conversations' },
  { hash: '65c9222', date: 'Mar 2026', text: 'The agent learns to log time, not just manage tasks' },
  { hash: '37a1e97', date: 'Jun 2026', text: 'A brand-new React interface' },
  { hash: '63a4ac0', date: 'Jun 2026', text: 'Analytics: see where your hours go' },
  { hash: '982be84', date: 'Aug 2026', text: 'Guardrails for the AI chat' },
  { hash: 'dd65920', date: 'Sep 2026', text: 'Deployed to Microsoft Azure' },
  { hash: 'b2dbc54', date: 'Sep 2026', text: 'Google sign-in, profiles, privacy-first legal pages' },
  { hash: 'b8d22e0', date: 'Sep 2026', text: 'Scope & safety guard for every message' },
  { hash: '99ddabd', date: 'Oct 2026', text: 'AI credits and fair-use limits — live at taskflowai.in' },
];

const STACK = ['React', 'TypeScript', 'Material UI', 'TanStack Query', 'Django', 'DRF', 'LangGraph', 'Azure OpenAI', 'PostgreSQL', 'Azure App Service'];

// ---------------------------------------------------------------------------

const SectionHeading: React.FC<{ eyebrow: string; title: React.ReactNode; subtitle?: string; dark?: boolean }> = ({
  eyebrow,
  title,
  subtitle,
  dark,
}) => (
  <Reveal sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto', mb: { xs: 5, md: 7 } }}>
    <Typography
      sx={{
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: '.14em',
        textTransform: 'uppercase',
        color: dark ? '#A5B4FC' : 'primary.main',
        mb: 1.5,
      }}
    >
      {eyebrow}
    </Typography>
    <Typography
      component="h2"
      sx={{
        fontSize: { xs: '2rem', md: '2.75rem' },
        fontWeight: 800,
        letterSpacing: '-0.03em',
        lineHeight: 1.1,
        color: dark ? '#fff' : 'text.primary',
      }}
    >
      {title}
    </Typography>
    {subtitle && (
      <Typography sx={{ mt: 2, fontSize: { xs: 16, md: 18 }, color: dark ? 'rgba(255,255,255,0.68)' : 'text.secondary' }}>
        {subtitle}
      </Typography>
    )}
  </Reveal>
);

const LogoTile: React.FC<{ size?: number }> = ({ size = 34 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: 2,
      display: 'grid',
      placeItems: 'center',
      bgcolor: 'primary.main',
      color: '#fff',
      boxShadow: `0 6px 18px -6px ${alpha('#4F46E5', 0.8)}`,
    }}
  >
    <BrandMark fontSize="small" />
  </Box>
);

const NavBar: React.FC<{ authed: boolean }> = ({ authed }) => {
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 12 });
  const links = [
    ['Features', '#features'],
    ['How it works', '#how-it-works'],
    ['Safety', '#safety'],
    ['The maker', '#maker'],
  ];

  return (
    <Box
      component="header"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10,
        transition: 'background-color .3s, border-color .3s, backdrop-filter .3s',
        bgcolor: scrolled ? alpha(INK, 0.78) : 'transparent',
        backdropFilter: scrolled ? 'blur(14px) saturate(140%)' : 'none',
        borderBottom: '1px solid',
        borderColor: scrolled ? 'rgba(255,255,255,0.08)' : 'transparent',
      }}
    >
      <Stack direction="row" alignItems="center" sx={{ ...CONTAINER, height: 68 }}>
        <Link component={RouterLink} to="/" underline="none" sx={{ color: '#fff' }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <LogoTile />
            <Typography sx={{ fontWeight: 800, fontSize: 19, letterSpacing: '-0.01em' }}>{BRAND.name}</Typography>
          </Stack>
        </Link>
        <Stack
          component="nav"
          aria-label="Page sections"
          direction="row"
          spacing={3.5}
          sx={{ mx: 'auto', display: { xs: 'none', md: 'flex' } }}
        >
          {links.map(([label, href]) => (
            <Link
              key={href}
              href={href}
              underline="none"
              sx={{ color: 'rgba(255,255,255,0.72)', fontSize: 14.5, fontWeight: 500, '&:hover': { color: '#fff' } }}
            >
              {label}
            </Link>
          ))}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: { xs: 'auto', md: 0 } }}>
          {authed ? (
            <Button component={RouterLink} to="/tasks" variant="contained" endIcon={<ArrowForwardRoundedIcon />}>
              Open app
            </Button>
          ) : (
            <>
              <Button component={RouterLink} to="/login" sx={{ color: '#fff', display: { xs: 'none', sm: 'inline-flex' } }}>
                Sign in
              </Button>
              <Button component={RouterLink} to="/login?mode=signup" variant="contained">
                Get started
              </Button>
            </>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

// ----------------------------- Hero ---------------------------------------

const Hero: React.FC<{ authed: boolean }> = ({ authed }) => (
  <Box
    component="section"
    sx={{
      position: 'relative',
      overflow: 'hidden',
      color: '#fff',
      bgcolor: INK,
      backgroundImage: `
        radial-gradient(55% 60% at 78% 18%, ${alpha('#4F46E5', 0.55)} 0%, transparent 70%),
        radial-gradient(40% 45% at 8% 85%, ${alpha('#0EA5E9', 0.25)} 0%, transparent 70%),
        radial-gradient(30% 30% at 45% 0%, ${alpha('#8B5CF6', 0.25)} 0%, transparent 70%)`,
      pt: { xs: 14, md: 18 },
      pb: { xs: 10, md: 16 },
      // faint grid, fading out towards the edges
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        backgroundImage:
          'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
        backgroundSize: '56px 56px',
        maskImage: 'radial-gradient(70% 60% at 50% 35%, #000 0%, transparent 75%)',
        WebkitMaskImage: 'radial-gradient(70% 60% at 50% 35%, #000 0%, transparent 75%)',
        pointerEvents: 'none',
      },
    }}
  >
    <Box
      sx={{
        ...CONTAINER,
        position: 'relative',
        display: 'grid',
        gap: { xs: 7, md: 6 },
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.1fr) minmax(0, 1fr)' },
        alignItems: 'center',
      }}
    >
      {/* Above the fold: animate on load rather than on scroll. */}
      <Box sx={{ animation: `${enter} .8s cubic-bezier(.2,.7,.2,1) both`, '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            display: 'inline-flex',
            pl: 0.75,
            pr: 1.75,
            py: 0.6,
            mb: 3,
            borderRadius: 99,
            border: '1px solid rgba(255,255,255,0.14)',
            bgcolor: 'rgba(255,255,255,0.05)',
            fontSize: 13.5,
            color: 'rgba(255,255,255,0.85)',
          }}
        >
          <Box
            sx={{
              px: 1,
              py: 0.2,
              borderRadius: 99,
              bgcolor: '#4F46E5',
              fontSize: 11.5,
              fontWeight: 700,
              letterSpacing: '.04em',
            }}
          >
            LIVE
          </Box>
          <span>Tasks · Time · AI — in one place</span>
        </Stack>

        <Typography
          component="h1"
          sx={{
            fontSize: { xs: '2.6rem', sm: '3.4rem', md: '3.3rem', lg: '4.1rem' },
            fontWeight: 800,
            lineHeight: 1.02,
            letterSpacing: '-0.045em',
          }}
        >
          Say it once.
          <Box component="span" sx={{ display: 'block', whiteSpace: { sm: 'nowrap' }, ...GRADIENT_TEXT }}>
            Consider it done.
          </Box>
        </Typography>

        <Typography sx={{ mt: 3, maxWidth: 520, fontSize: { xs: 17, md: 19 }, lineHeight: 1.6, color: 'rgba(255,255,255,0.72)' }}>
          {BRAND.name} is a task manager and time tracker with an AI assistant that does the busywork for you —
          creating tasks, logging hours and organizing categories from a single sentence.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4.5, '& .MuiButton-root': { whiteSpace: 'nowrap' } }}>
          {authed ? (
            <Button
              component={RouterLink}
              to="/tasks"
              variant="contained"
              size="large"
              endIcon={<ArrowForwardRoundedIcon />}
              sx={{ px: 3.5, py: 1.5, fontSize: 16, boxShadow: `0 12px 30px -10px ${alpha('#6366F1', 0.9)}` }}
            >
              Open {BRAND.name}
            </Button>
          ) : (
            <Button
              component={RouterLink}
              to="/login?mode=signup"
              variant="contained"
              size="large"
              endIcon={<ArrowForwardRoundedIcon />}
              sx={{ px: 3.5, py: 1.5, fontSize: 16, boxShadow: `0 12px 30px -10px ${alpha('#6366F1', 0.9)}` }}
            >
              Get started — it’s free
            </Button>
          )}
          <Button
            href="#how-it-works"
            size="large"
            sx={{
              px: 3,
              py: 1.5,
              fontSize: 16,
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.35)' },
            }}
          >
            See how it works
          </Button>
        </Stack>

        <Stack direction="row" spacing={2.5} sx={{ mt: 3.5, color: 'rgba(255,255,255,0.55)', fontSize: 13.5 }} flexWrap="wrap" useFlexGap>
          {['Free to use', 'Sign in with Google', 'No ads, no trackers'].map((t) => (
            <Stack key={t} direction="row" spacing={0.6} alignItems="center">
              <CheckRoundedIcon sx={{ fontSize: 17, color: '#38BDF8' }} />
              <span>{t}</span>
            </Stack>
          ))}
        </Stack>
      </Box>

      <Box
        sx={{
          pl: { md: 2, lg: 4 },
          animation: `${enter} .9s cubic-bezier(.2,.7,.2,1) .15s both`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        <HeroDemo />
      </Box>
    </Box>
  </Box>
);

// --------------------------- Prompt marquee --------------------------------

const MarqueeRow: React.FC<{ items: string[]; reverse?: boolean; reduced: boolean }> = ({ items, reverse, reduced }) => {
  // Two copies side by side; the track slides by exactly one copy for a seamless loop.
  const track = reduced ? items : items.concat(items);
  return (
    <Box sx={{ overflow: 'hidden', '&:hover > div': { animationPlayState: 'paused' } }}>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          width: reduced ? 'auto' : 'max-content',
          flexWrap: reduced ? 'wrap' : 'nowrap',
          justifyContent: reduced ? 'center' : 'flex-start',
          gap: reduced ? 1.5 : 0,
          animation: reduced ? 'none' : `${marquee} ${items.length * 7}s linear infinite`,
          animationDirection: reverse ? 'reverse' : 'normal',
          pr: 1.5,
        }}
        useFlexGap={reduced}
      >
        {track.map((p, i) => (
          <Stack
            key={`${p}-${i}`}
            aria-hidden={i >= items.length}
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              flexShrink: 0,
              px: 2,
              py: 1.1,
              borderRadius: 99,
              bgcolor: '#fff',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 1px 2px rgba(16,19,34,0.04)',
              fontSize: 15,
              color: 'text.primary',
              whiteSpace: 'nowrap',
            }}
          >
            <AutoAwesomeRoundedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
            <span>{p}</span>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

const PromptMarquee: React.FC = () => {
  const reduced = usePrefersReducedMotion();
  return (
    <Box component="section" aria-label="Example requests" sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.default' }}>
      <Typography
        sx={{ textAlign: 'center', mb: 3, fontSize: 14, fontWeight: 600, color: 'text.secondary', px: 2 }}
      >
        Things you can just <Box component="span" sx={{ color: 'primary.main' }}>ask</Box>:
      </Typography>
      <Stack
        spacing={1.5}
        sx={{
          maskImage: 'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)',
          WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)',
          px: reduced ? 2 : 0,
        }}
      >
        <MarqueeRow items={PROMPTS_A} reduced={reduced} />
        <MarqueeRow items={PROMPTS_B} reverse reduced={reduced} />
      </Stack>
    </Box>
  );
};

// ------------------------------ Features -----------------------------------

const BentoCard: React.FC<{
  icon: React.ReactNode;
  accent: string;
  title: string;
  text: string;
  delay?: number;
  span: number;
  dark?: boolean;
  children?: React.ReactNode;
}> = ({ icon, accent, title, text, delay, span, dark, children }) => (
  <Reveal
    delay={delay}
    sx={{
      gridColumn: { xs: 'span 1', md: `span ${span}` },
      display: 'flex',
      flexDirection: 'column',
      p: { xs: 3, md: 3.5 },
      borderRadius: 5,
      border: '1px solid',
      borderColor: dark ? 'rgba(255,255,255,0.08)' : 'divider',
      bgcolor: dark ? INK : '#fff',
      color: dark ? '#fff' : 'text.primary',
      position: 'relative',
      overflow: 'hidden',
      transition: 'box-shadow .3s, transform .3s',
      '&:hover': { boxShadow: '0 24px 48px -28px rgba(16,19,34,0.35)' },
      ...(dark && {
        backgroundImage: `radial-gradient(60% 80% at 100% 0%, ${alpha('#4F46E5', 0.5)}, transparent 70%)`,
      }),
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: 2.5,
        display: 'grid',
        placeItems: 'center',
        color: accent,
        bgcolor: alpha(accent, dark ? 0.2 : 0.1),
        mb: 2.5,
      }}
    >
      {icon}
    </Box>
    <Typography component="h3" sx={{ fontSize: 21, fontWeight: 700, letterSpacing: '-0.015em' }}>
      {title}
    </Typography>
    <Typography sx={{ mt: 1, color: dark ? 'rgba(255,255,255,0.7)' : 'text.secondary', lineHeight: 1.6 }}>{text}</Typography>
    {children && <Box sx={{ mt: 'auto', pt: 3 }}>{children}</Box>}
  </Reveal>
);

const AiVisual = () => (
  <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
    {[
      'Create, edit and complete tasks',
      'Log time against any task',
      'Sort work into categories',
      'Answer “what’s due?” in seconds',
      'Remember the conversation',
      'Confirm before risky deletes',
    ].map((t) => (
      <Stack key={t} direction="row" spacing={1.25} alignItems="center">
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha('#38BDF8', 0.18),
            color: '#38BDF8',
            flexShrink: 0,
          }}
        >
          <CheckRoundedIcon sx={{ fontSize: 15 }} />
        </Box>
        <Typography sx={{ fontSize: 15, color: 'rgba(255,255,255,0.88)' }}>{t}</Typography>
      </Stack>
    ))}
  </Box>
);

const TasksVisual = () => (
  <Stack spacing={1}>
    {[
      { t: 'Ship landing page', p: 'High', c: '#DC2626', done: false },
      { t: 'Reply to design feedback', p: 'Medium', c: '#D97706', done: false },
      { t: 'Renew domain', p: 'Low', c: '#16A34A', done: true },
    ].map((r) => (
      <Stack
        key={r.t}
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{ px: 1.5, py: 1, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', bgcolor: r.done ? '#FAFAFD' : '#fff' }}
      >
        {r.done ? (
          <CheckBoxRoundedIcon sx={{ fontSize: 20, color: '#818CF8' }} />
        ) : (
          <CheckBoxOutlineBlankRoundedIcon sx={{ fontSize: 20, color: '#9AA1B5' }} />
        )}
        <Typography
          sx={{
            flex: 1,
            fontSize: 14,
            fontWeight: 600,
            color: r.done ? 'text.secondary' : 'text.primary',
            textDecoration: r.done ? 'line-through' : 'none',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {r.t}
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.3}
          sx={{ px: 0.8, borderRadius: 99, border: `1px solid ${alpha(r.c, 0.45)}`, color: r.c, fontSize: 11.5, fontWeight: 600 }}
        >
          <FlagRoundedIcon sx={{ fontSize: 12 }} />
          <span>{r.p}</span>
        </Stack>
      </Stack>
    ))}
  </Stack>
);

const TimeVisual = () => (
  <Box>
    <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.25, fontSize: 13 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Today</Typography>
      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Total: 3h 45m</Typography>
    </Stack>
    <Stack spacing={1}>
      {[
        ['2h', 'API integration', 1],
        ['1h 15m', 'Frontend development', 0.62],
        ['30m', 'Stand-up', 0.3],
      ].map(([d, t, w]) => (
        <Stack key={t as string} direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              minWidth: 62,
              textAlign: 'center',
              py: 0.35,
              borderRadius: 99,
              bgcolor: alpha('#4F46E5', 0.35 + 0.65 * (w as number)),
              color: '#fff',
              fontSize: 12.5,
              fontWeight: 700,
            }}
          >
            {d}
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
            {t}
          </Typography>
        </Stack>
      ))}
    </Stack>
  </Box>
);

const BARS = [
  [0.35, 0.2, 0.1],
  [0.5, 0.15, 0.2],
  [0.25, 0.3, 0.05],
  [0.6, 0.2, 0.15],
  [0.45, 0.35, 0.1],
  [0.15, 0.1, 0.05],
  [0.3, 0.1, 0.25],
];
const BAR_COLORS = ['#4F46E5', '#0EA5E9', '#F59E0B'];

const AnalyticsVisual = () => {
  const [ref, inView] = useInView<HTMLDivElement>(0.4);
  const reduced = usePrefersReducedMotion();
  const grown = inView || reduced;
  return (
    <Box>
      <Stack ref={ref} direction="row" alignItems="flex-end" spacing={1.25} sx={{ height: 120, px: 0.5 }}>
        {BARS.map((stack, i) => (
          <Stack
            key={i}
            direction="column-reverse"
            sx={{
              flex: 1,
              height: '100%',
              justifyContent: 'flex-start',
              transformOrigin: 'bottom',
              transform: grown ? 'scaleY(1)' : 'scaleY(0)',
              transition: reduced ? 'none' : `transform .8s cubic-bezier(.2,.8,.2,1) ${i * 70}ms`,
            }}
          >
            {stack.map((h, j) => (
              <Box
                key={j}
                sx={{
                  height: `${h * 100}%`,
                  bgcolor: BAR_COLORS[j],
                  borderTopLeftRadius: j === stack.length - 1 ? 4 : 0,
                  borderTopRightRadius: j === stack.length - 1 ? 4 : 0,
                }}
              />
            ))}
          </Stack>
        ))}
      </Stack>
      <Stack direction="row" spacing={1.25} sx={{ mt: 1, px: 0.5 }}>
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
          <Typography key={i} sx={{ flex: 1, textAlign: 'center', fontSize: 11.5, color: 'text.secondary' }}>
            {d}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
};

const CategoriesVisual = () => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
    {[
      ['Work', '#4F46E5', '8 tasks'],
      ['Personal', '#D97706', '3 tasks'],
      ['Learning', '#16A34A', '5 tasks'],
      ['Side project', '#0EA5E9', '2 tasks'],
    ].map(([n, c, k]) => (
      <Box key={n} sx={{ p: 1.25, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', borderTop: `3px solid ${c}` }}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <FolderRoundedIcon sx={{ fontSize: 17, color: c }} />
          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }} noWrap>
            {n}
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>{k}</Typography>
      </Box>
    ))}
  </Box>
);

const Features: React.FC = () => (
  <Box component="section" id="features" sx={{ scrollMarginTop: 72, py: { xs: 10, md: 14 }, bgcolor: 'background.default' }}>
    <Box sx={CONTAINER}>
      <SectionHeading
        eyebrow="Everything in one place"
        title={
          <>
            Plan it, track it, <Box component="span" sx={{ color: 'primary.main' }}>understand it.</Box>
          </>
        }
        subtitle="Five tools that usually live in five different apps — wired together, and all reachable through one conversation."
      />
      <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(6, 1fr)' } }}>
        <BentoCard
          span={4}
          dark
          accent="#A5B4FC"
          icon={<AutoAwesomeRoundedIcon />}
          title="An AI assistant with real hands"
          text="It doesn't just chat — it uses the same tools you do. Ask for something in plain language and watch it happen in your workspace."
        >
          <AiVisual />
        </BentoCard>
        <BentoCard
          span={2}
          delay={80}
          accent="#4F46E5"
          icon={<ChecklistRoundedIcon />}
          title="Tasks"
          text="Priorities, due dates and categories, with search and filters that keep the list honest."
        >
          <TasksVisual />
        </BentoCard>
        <BentoCard
          span={2}
          accent="#0EA5E9"
          icon={<ScheduleRoundedIcon />}
          title="Time logs"
          text="Log hours against tasks and see each day add up."
        >
          <TimeVisual />
        </BentoCard>
        <BentoCard
          span={2}
          delay={80}
          accent="#F59E0B"
          icon={<BarChartRoundedIcon />}
          title="Analytics"
          text="By day, week, month or year — split by category or task."
        >
          <AnalyticsVisual />
        </BentoCard>
        <BentoCard
          span={2}
          delay={160}
          accent="#16A34A"
          icon={<FolderRoundedIcon />}
          title="Categories"
          text="Group work your way and see tasks and hours per bucket."
        >
          <CategoriesVisual />
        </BentoCard>
      </Box>
    </Box>
  </Box>
);

// ----------------------------- How it works --------------------------------

const HowItWorks: React.FC = () => (
  <Box component="section" id="how-it-works" sx={{ scrollMarginTop: 72, py: { xs: 10, md: 14 }, bgcolor: '#fff' }}>
    <Box sx={CONTAINER}>
      <SectionHeading
        eyebrow="How it works"
        title="From sentence to done in three steps"
        subtitle="Behind the chat box is an agent that plans, calls tools and reports back — the same validation rules as the rest of the app."
      />
      <Box sx={{ position: 'relative', display: 'grid', gap: { xs: 5, md: 4 }, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
        {/* dashed connector behind the step badges */}
        <Box
          aria-hidden
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            top: 36,
            left: '16%',
            right: '16%',
            borderTop: '2px dashed',
            borderColor: alpha('#4F46E5', 0.25),
          }}
        />
        {STEPS.map((s, i) => (
          <Reveal key={s.title} delay={i * 140} sx={{ textAlign: 'center', position: 'relative', px: { md: 2 } }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                mx: 'auto',
                borderRadius: '22px',
                display: 'grid',
                placeItems: 'center',
                color: '#fff',
                background: 'linear-gradient(140deg, #6366F1, #4F46E5 55%, #3730A3)',
                boxShadow: `0 16px 30px -12px ${alpha('#4F46E5', 0.7)}, 0 0 0 8px #fff`,
                position: 'relative',
                '& svg': { fontSize: 32 },
              }}
            >
              {s.icon}
              <Box
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  bgcolor: INK,
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: 800,
                  display: 'grid',
                  placeItems: 'center',
                  border: '2px solid #fff',
                }}
              >
                {i + 1}
              </Box>
            </Box>
            <Typography component="h3" sx={{ mt: 3, fontSize: 20, fontWeight: 700 }}>
              {s.title}
            </Typography>
            <Typography sx={{ mt: 1, color: 'text.secondary', lineHeight: 1.65, maxWidth: 340, mx: 'auto' }}>
              {s.text}
            </Typography>
          </Reveal>
        ))}
      </Box>
    </Box>
  </Box>
);

// ------------------------------- Safety ------------------------------------

const Safety: React.FC = () => (
  <Box
    component="section"
    id="safety"
    sx={{
      scrollMarginTop: 72,
      py: { xs: 10, md: 14 },
      bgcolor: INK,
      backgroundImage: `radial-gradient(50% 50% at 50% 0%, ${alpha('#4F46E5', 0.35)}, transparent 70%)`,
    }}
  >
    <Box sx={CONTAINER}>
      <SectionHeading
        dark
        eyebrow="Built with guardrails"
        title={
          <>
            An AI you can <Box component="span" sx={GRADIENT_TEXT}>actually trust</Box> with your list
          </>
        }
        subtitle="Giving an AI the keys to your data deserves care. These protections are built into how TaskFlow works, not bolted on."
      />
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' } }}>
        {TRUST.map((c, i) => (
          <Reveal
            key={c.title}
            delay={(i % 3) * 100}
            sx={{
              p: 3,
              borderRadius: 4,
              border: '1px solid rgba(255,255,255,0.08)',
              bgcolor: 'rgba(255,255,255,0.03)',
              transition: 'border-color .3s, background-color .3s',
              '&:hover': { borderColor: alpha('#A5B4FC', 0.4), bgcolor: 'rgba(255,255,255,0.05)' },
            }}
          >
            <Box sx={{ color: '#A5B4FC', mb: 1.75, '& svg': { fontSize: 28 } }}>{c.icon}</Box>
            <Typography component="h3" sx={{ color: '#fff', fontWeight: 700, fontSize: 17.5 }}>
              {c.title}
            </Typography>
            <Typography sx={{ mt: 0.75, color: 'rgba(255,255,255,0.64)', fontSize: 15, lineHeight: 1.65 }}>{c.text}</Typography>
          </Reveal>
        ))}
      </Box>
    </Box>
  </Box>
);

// -------------------------------- Maker ------------------------------------

const Maker: React.FC = () => {
  const reduced = usePrefersReducedMotion();
  return (
    <Box component="section" id="maker" sx={{ scrollMarginTop: 72, py: { xs: 10, md: 14 }, bgcolor: 'background.default' }}>
      <Box
        sx={{
          ...CONTAINER,
          display: 'grid',
          gap: { xs: 5, md: 7 },
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 0.9fr) minmax(0, 1.1fr)' },
          alignItems: 'center',
        }}
      >
        <Reveal>
          <Typography
            sx={{ fontSize: 13, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: 'primary.main', mb: 1.5 }}
          >
            The maker
          </Typography>


          <Stack direction="row" spacing={2.25} alignItems="center" sx={{ mt: 4 }}>
            <Box
              sx={{
                p: '3px',
                borderRadius: '50%',
                background: 'conic-gradient(from 180deg, #4F46E5, #38BDF8, #C4B5FD, #4F46E5)',
                animation: reduced ? 'none' : `${float} 6s ease-in-out infinite`,
              }}
            >
              <Box
                sx={{
                  width: 76,
                  height: 76,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: '#4F46E5',
                  color: '#fff',
                  fontSize: 28,
                  fontWeight: 800,
                  border: '3px solid #F4F5FA',
                }}
              >
                {MAKER.initials}
              </Box>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em' }}>{MAKER.name}</Typography>
              <Typography sx={{ color: 'text.secondary' }}>Designed, built and shipped {BRAND.name}</Typography>
            </Box>
          </Stack>

          <Typography sx={{ mt: 3, color: 'text.secondary', fontSize: 17, lineHeight: 1.7, maxWidth: 520 }}>
            {BRAND.name} pairs a React interface with a Django API and a LangGraph agent that turns your sentences
            into real actions, kept on task by safety guardrails and running on Microsoft Azure.
          </Typography>

          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mt: 3 }}>
            {STACK.map((s) => (
              <Box
                key={s}
                sx={{
                  px: 1.4,
                  py: 0.5,
                  borderRadius: 99,
                  bgcolor: '#fff',
                  border: '1px solid',
                  borderColor: 'divider',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {s}
              </Box>
            ))}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2.5, color: 'text.secondary', maxWidth: 520 }}>
            <AutoAwesomeRoundedIcon sx={{ fontSize: 16, color: 'primary.light', flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13.5, lineHeight: 1.5 }}>
              Pair-programmed with Claude Code
            </Typography>
          </Stack>

          <Button
            href={MAKER.linkedIn}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            size="large"
            startIcon={<LinkedInIcon />}
            endIcon={<NorthEastRoundedIcon sx={{ fontSize: '16px !important' }} />}
            sx={{
              mt: 4,
              px: 3,
              py: 1.4,
              bgcolor: '#0A66C2',
              '&:hover': { bgcolor: '#004182' },
            }}
          >
            Connect with {MAKER.name.split(' ')[0]} on LinkedIn
          </Button>
        </Reveal>

        {/* Build log, styled as a terminal */}
        <Reveal delay={150}>
          <Box
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              bgcolor: INK,
              border: '1px solid rgba(255,255,255,0.06)',
              boxShadow: '0 40px 80px -30px rgba(16,19,34,0.6)',
              fontFamily: '"JetBrains Mono", "Cascadia Code", Consolas, "SFMono-Regular", Menlo, monospace',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ px: 2, py: 1.4, borderBottom: '1px solid rgba(255,255,255,0.07)' }}
            >
              {['#FF5F57', '#FEBC2E', '#28C840'].map((c) => (
                <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c }} />
              ))}
              <Typography sx={{ flex: 1, textAlign: 'center', fontSize: 12.5, color: 'rgba(255,255,255,0.45)', fontFamily: 'inherit', pr: 5 }}>
                ~/taskflow — build log
              </Typography>
            </Stack>
            <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5, fontSize: { xs: 12.5, sm: 13.5 }, lineHeight: 1.6 }}>
              <Box sx={{ color: 'rgba(255,255,255,0.9)', mb: 1.5 }}>
                <Box component="span" sx={{ color: '#28C840' }}>
                  sajal@taskflow
                </Box>
                <Box component="span" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                  :~$
                </Box>{' '}
                git log --reverse --oneline
              </Box>
              <Stack spacing={0.9} component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {BUILD_LOG.map((c) => (
                  <Stack key={c.hash} component="li" direction="row" spacing={1.5} alignItems="baseline">
                    <Box component="span" sx={{ color: '#FBBF24', flexShrink: 0 }}>
                      {c.hash}
                    </Box>
                    <Box component="span" sx={{ color: '#38BDF8', flexShrink: 0, minWidth: 64, display: { xs: 'none', sm: 'inline' } }}>
                      {c.date}
                    </Box>
                    <Box component="span" sx={{ color: 'rgba(255,255,255,0.82)' }}>
                      {c.text}
                    </Box>
                  </Stack>
                ))}
              </Stack>
              <Box sx={{ mt: 1.5, color: 'rgba(255,255,255,0.9)' }}>
                <Box component="span" sx={{ color: '#28C840' }}>
                  sajal@taskflow
                </Box>
                <Box component="span" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                  :~$
                </Box>{' '}
                <Box component="span" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                  # still shipping…
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    width: 8,
                    height: '1.05em',
                    ml: 0.75,
                    verticalAlign: '-3px',
                    bgcolor: '#A5B4FC',
                    animation: reduced ? 'none' : `${blink} 1.1s step-end infinite`,
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Reveal>
      </Box>
    </Box>
  );
};

// ------------------------------ Final CTA ----------------------------------

const FinalCta: React.FC<{ authed: boolean }> = ({ authed }) => (
  <Box component="section" sx={{ pb: { xs: 10, md: 14 }, bgcolor: 'background.default' }}>
    <Box sx={CONTAINER}>
      <Reveal
        sx={{
          position: 'relative',
          overflow: 'hidden',
          textAlign: 'center',
          px: { xs: 3, md: 8 },
          py: { xs: 7, md: 9 },
          borderRadius: 6,
          color: '#fff',
          background: 'linear-gradient(135deg, #312E81 0%, #4F46E5 50%, #0EA5E9 130%)',
          boxShadow: `0 40px 80px -40px ${alpha('#4F46E5', 0.8)}`,
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            right: { xs: -60, md: 40 },
            top: { xs: -40, md: -30 },
            color: 'rgba(255,255,255,0.08)',
            '& svg': { fontSize: 260 },
          }}
        >
          <BrandMark />
        </Box>
        <Typography
          component="h2"
          sx={{ position: 'relative', fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.08 }}
        >
          Your next task is
          <br />
          one sentence away.
        </Typography>
        <Typography sx={{ position: 'relative', mt: 2, fontSize: 18, color: 'rgba(255,255,255,0.8)' }}>
          Free to start. Sign in with Google and the assistant is ready when you are.
        </Typography>
        <Button
          component={RouterLink}
          to={authed ? '/assistant' : '/login?mode=signup'}
          size="large"
          endIcon={<ArrowForwardRoundedIcon />}
          sx={{
            position: 'relative',
            mt: 4,
            px: 4,
            py: 1.6,
            fontSize: 16,
            bgcolor: '#fff',
            color: 'primary.dark',
            '&:hover': { bgcolor: '#EEF0FF' },
          }}
        >
          {authed ? 'Ask the assistant' : 'Create your free account'}
        </Button>
      </Reveal>
    </Box>
  </Box>
);

// -------------------------------- Footer -----------------------------------

const Footer: React.FC = () => {
  const linkSx = { color: 'rgba(255,255,255,0.65)', fontSize: 14.5, '&:hover': { color: '#fff' } };
  return (
    <Box component="footer" sx={{ bgcolor: INK, color: '#fff', pt: 8, pb: 4 }}>
      <Box sx={CONTAINER}>
        <Box sx={{ display: 'grid', gap: 5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '2fr 1fr 1fr 1fr' } }}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <LogoTile />
              <Typography sx={{ fontWeight: 800, fontSize: 19 }}>{BRAND.name}</Typography>
            </Stack>
            <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.55)', maxWidth: 300, fontSize: 14.5, lineHeight: 1.6 }}>
              {BRAND.tagline}
            </Typography>
          </Box>
          {[
            {
              title: 'Product',
              links: [
                { label: 'Features', href: '#features' },
                { label: 'How it works', href: '#how-it-works' },
                { label: 'Safety', href: '#safety' },
                { label: 'Sign in', to: '/login' },
              ],
            },
            {
              title: 'Legal',
              links: [
                { label: 'Terms of Service', to: '/terms' },
                { label: 'Privacy Policy', to: '/privacy' },
                { label: 'Delete your data', to: '/privacy#delete-your-data' },
              ],
            },
            {
              title: 'The maker',
              links: [
                { label: MAKER.name, href: '#maker' },
                { label: 'LinkedIn ↗', href: MAKER.linkedIn, external: true },
                { label: 'Contact', href: `mailto:${LEGAL.contactEmail}` },
              ],
            },
          ].map((col) => (
            <Box key={col.title} component="nav" aria-label={col.title}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', mb: 2 }}>
                {col.title}
              </Typography>
              <Stack spacing={1.25}>
                {col.links.map((l) =>
                  'to' in l && l.to ? (
                    <Link key={l.label} component={RouterLink} to={l.to} underline="none" sx={linkSx}>
                      {l.label}
                    </Link>
                  ) : (
                    <Link
                      key={l.label}
                      href={'href' in l ? l.href : undefined}
                      underline="none"
                      sx={linkSx}
                      {...('external' in l && l.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    >
                      {l.label}
                    </Link>
                  )
                )}
              </Stack>
            </Box>
          ))}
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mt: 7, pt: 3, borderTop: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.45)', fontSize: 13.5 }}
        >
          <span>
            © {new Date().getFullYear()} {BRAND.name}. Built by{' '}
            <Link href={MAKER.linkedIn} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: 'rgba(255,255,255,0.75)' }}>
              {MAKER.name}
            </Link>{' '}
            in Goa, India.
          </span>
          <Stack direction="row" spacing={2.5}>
            <Link component={RouterLink} to="/terms" underline="hover" sx={{ color: 'inherit' }}>
              Terms of Service
            </Link>
            <Link component={RouterLink} to="/privacy" underline="hover" sx={{ color: 'inherit' }}>
              Privacy Policy
            </Link>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------

/** Public marketing page at `/`. Signed-in visitors see it too, with "Open app" CTAs. */
const LandingPage: React.FC = () => {
  const authed = isAuthenticated();

  useEffect(() => {
    const previous = document.title;
    document.title = `${BRAND.name} — Tasks, time tracking and an AI assistant`;
    // Deep links such as /#maker: the section only exists after render, so the
    // browser's own jump on load misses it.
    const target = window.location.hash && document.getElementById(window.location.hash.slice(1));
    if (target) target.scrollIntoView();
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <Box sx={{ bgcolor: INK, overflowX: 'hidden' }}>
      <GlobalStyles styles={{ '@media (prefers-reduced-motion: no-preference)': { html: { scrollBehavior: 'smooth' } } }} />
      <NavBar authed={authed} />
      <Box component="main">
        <Hero authed={authed} />
        <PromptMarquee />
        <Features />
        <HowItWorks />
        <Safety />
        <Maker />
        <FinalCta authed={authed} />
      </Box>
      <Footer />
    </Box>
  );
};

export default LandingPage;
